import shortid from 'shortid';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import groupBy from 'lodash/groupBy';
import sum from 'lodash/sum';
import isEqual from 'lodash/isEqual';
import difference from 'utils/difference';
import { put, fork, take, select, race } from 'redux-saga/effects';
import { takeFetch } from 'redux/helpers';
import * as serviceActions from '../../../../services/actions';
import * as actions from './actions';


function* upsertTags() {
  const { success } = yield takeFetch(serviceActions.api.configuration.project.UPSERT_TAGS);
  if (success) {
    const { projectId } = yield select(state => state.configuration.project.editor.tag);
    yield put(actions.init.begin({ projectId }));
    ttb.Notifications.ShowSaveSuccessMessage('Project Configuration', 'All the changes have been saved');
  } else {
    ttb.Notifications.ShowErrorMessage('Project Configuration', 'The changes have not been saved');
  }
}

function* getTags() {
  const { success } = yield takeFetch(serviceActions.api.configuration.project.GET_TAGS);
  if (success) {
    const tagOptions = success.data.map(value => ({
      value: cloneDeep(value),
      $$localId: shortid.generate(),
      changes: 0,
    }));
    yield put(actions.init.end({ tagOptions, tags: success.data }));
  }
}

function* init() {
  while (true) {
    const { projectId } = yield take(actions.INIT.BEGIN);
    yield put(serviceActions.api.configuration.project.getTags.request({
      projectId,
    }));
    yield fork(getTags);
  }
}

function* save() {
  while (true) {
    yield take(actions.SAVE.BEGIN);
    const { projectId, tagOptions } = yield select(state => state.configuration.project.editor.tag);
    yield put(serviceActions.api.configuration.project.upsertTags.request({
      projectId,
      tags: tagOptions.filter(x => x.changes).map(x => x.value),
    }));
    yield fork(upsertTags);
  }
}

function* add() {
  while (true) {
    yield take(actions.ADD.BEGIN);
    const { tagOptions: previosTags } = yield select(state => state.configuration.project.editor.tag);
    const tagOptions = [
      ...previosTags,
      {
        $$localId: shortid.generate(),
        changes: 0,
        value: {
          name: `New Tag ${previosTags.length + 1}`,
        },
      },
    ];
    yield put(actions.add.end({ tagOptions }));
  }
}

function* remove() {
  while (true) {
    const { localId } = yield take(actions.REMOVE.BEGIN);
    const { tagOptions: previosTags } = yield select(state => state.configuration.project.editor.tag);
    const tagOptions = previosTags.filter(x => x.$$localId !== localId);
    yield put(actions.remove.end({ tagOptions }));
  }
}

function* setRemoved() {
  while (true) {
    const { localId, value } = yield take(actions.SET_REMOVED.BEGIN);
    const { tagOptions: previosTags } = yield select(state => state.configuration.project.editor.tag);
    const tagOptions = previosTags.map((x) => {
      if (x.$$localId === localId) {
        return {
          ...x,
          value: {
            ...x.value,
            isRemoved: value,
          },
        };
      }
      return x;
    });
    yield put(actions.setRemoved.end({ tagOptions }));
  }
}

function* setName() {
  while (true) {
    const { localId, name } = yield take(actions.SET_NAME.BEGIN);
    const { tagOptions: previosTags } = yield select(state => state.configuration.project.editor.tag);
    const tagOptions = previosTags.map((x) => {
      if (x.$$localId === localId) {
        return {
          ...x,
          value: {
            ...x.value,
            name,
          },
        };
      }
      return x;
    });
    yield put(actions.setName.end({ tagOptions }));
  }
}

function* invalidate() {
  while (true) {
    yield race([
      take(actions.SET_NAME.END),
      take(actions.ADD.END),
      take(actions.REMOVE.END),
      take(actions.SET_REMOVED.END),
    ]);
    const {
      tags,
      tagOptions: previosTags,
       } = yield select(state => state.configuration.project.editor.tag);
    const newTags = previosTags.map((x) => {
      const tagValue = find(tags, y => y.id === x.value.id);
      const changes = tagValue ? difference(x.value, tagValue) : 1;
      return x.changes === changes ? x : {
        ...x,
        changes,
      };
    });
    const tagOptions = isEqual(newTags, previosTags)
      ? previosTags
      : newTags;
    const allChanges = sum(tagOptions.map(x => x.changes));
    yield put(actions.invalidate({ tagOptions, changes: allChanges }));
    yield put(actions.checkValidation.begin());
  }
}

function* resetChanges() {
  while (true) {
    yield take(actions.RESET_CHANGES.BEGIN);
    const {
      tags,
       } = yield select(state => state.configuration.project.editor.tag);
    const tagOptions = tags.map(value => ({
      value: cloneDeep(value),
      $$localId: shortid.generate(),
      changes: 0,
    }));
    yield put(actions.resetChanges.end({ tagOptions }));
  }
}

function* checkValidation() {
  while (true) {
    yield take(actions.CHECK_VALIDATION.BEGIN);
    const {
      tagOptions: previosTags,
       } = yield select(state => state.configuration.project.editor.tag);

    const tagsByName = groupBy(previosTags.filter(x => !x.value.isRemoved), 'value.name');
    const tagsErrors = Object.keys(tagsByName)
      .map(name => ({ tags: tagsByName[name] }))
      .filter(x => x.tags.length > 1);

    let errors;
    if (tagsErrors.length > 0) {
      errors = {
        tagsMap: tagsErrors.reduce((pre, val) => {
          val.tags.reduce((pre2, val2) => {
            pre2[val2.$$localId] = true;
            return pre2;
          }, pre);
          return pre;
        }, {}),
      };
    }
    yield put(actions.checkValidation.end({ errors }));
  }
}


export default function* tag() {
  yield fork(init);
  yield fork(save);
  yield fork(add);
  yield fork(remove);
  yield fork(setRemoved);
  yield fork(setName);
  yield fork(invalidate);
  yield fork(resetChanges);
  yield fork(checkValidation);
}



// WEBPACK FOOTER //
// ../redux/store/configuration/project/editor/tag/saga.js